import { memo, ReactElement, useCallback, useMemo } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { Table, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, FeeProfile } from '@pmp/api';
import { useTableView } from '@pmp/hooks';
import { Guard } from '@ohpen/react-authz';
import { lockActionTitle } from '@pmp/utils';

import { advisersColumns } from '../tableconfig';
import { AdviserListProps } from '../interfaces';

const entityType = ActionEntityType.BROKERS;
const rowKey = 'id';

export const AdvisersList = memo(({ data, isFetching, valueLists, onCreate, onView, onEdit, onDelete }: AdviserListProps): ReactElement => {
  const { tableViews, saveTableView, deleteTableView } = useTableView(entityType);
  const { t } = useTranslation();

  const getRowActions: (record: FeeProfile) => ReactElement[] = useCallback(
    record => {
      const rowActions = [
        <Tooltip key="view" title={t('global.buttons.view')} placement="top">
          <Button type="text" aria-label="View Adviser" icon={<EyeOutlined />} onClick={() => onView(record)} />
        </Tooltip>,
        <Guard key="edit" permissions={['brokers:update']} flags={['enableBrokersCrud']}>
          <Tooltip title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
            <Button
              type="text"
              aria-label="Edit Adviser"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              disabled={!!record.lockedByActionId}
            />
          </Tooltip>
        </Guard>,
        <Guard key="delete" permissions={['brokers:delete']} flags={['enableBrokersCrud']}>
          <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
            <Button
              type="text"
              aria-label="Delete Adviser"
              icon={<DeleteOutlined key="delete" />}
              onClick={() => onDelete(record)}
              disabled={!!record.lockedByActionId}
            />
          </Tooltip>
        </Guard>,
      ];

      return rowActions;
    },
    [onDelete, onEdit, onView, t],
  );

  const tableProps: TableProps<ActionEntityType, any, unknown, unknown, unknown> = useMemo(
    () => ({
      id: entityType,
      dataSource: data ?? [],
      columns: advisersColumns,
      rowKey,
      pagination: false,
      scroll: { x: 'max-content' },
      loading: isFetching,
      getRowActions,
      valueLists,
      showViews: true,
      tableViews,
      saveTableView,
      deleteTableView,
    }),
    [data, isFetching, getRowActions, valueLists, tableViews, saveTableView, deleteTableView],
  );

  return (
    <Table {...tableProps}>
      <Table.ActionsBar>
        <Flex justify="space-between">
          <Space size="middle">
            <Table.GlobalSearch />
          </Space>
          <Space size="middle">
            <Table.ResetFiltersButton />
            <Table.SelectColumnsButton />
            <Guard permissions={['brokers:create']} flags={['enableBrokersCrud']}>
              <Table.CreateButton onClick={onCreate} />
            </Guard>
          </Space>
        </Flex>
      </Table.ActionsBar>
    </Table>
  );
});
